import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  Clock,
  Search,
  Settings,
  Bell,
  BellOff,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  Download,
  Plus,
  Zap,
  Activity
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { formatRelativeTime } from '../utils/format'
import type { SocketCloudAlertsMonitoringComponents } from '@aaimplatform/socket-cloud-types';
// Console UI layer: category uses lowercase strings, canonical uses uppercase enum;
// timestamp initialized as Date objects, canonical expects ISO string;
// message is required in component filter code, canonical marks it optional;
// severity renders a lowercase display vocabulary (critical/high/medium/low/info),
// whereas the canonical Alert.severity is the AlertSeverity enum (UPPERCASE, matching
// the DB) after the prisma-arc-03 reconciliation -- kept as a display string here.
type Alert = Omit<SocketCloudAlertsMonitoringComponents['schemas']['Alert'], 'category' | 'timestamp' | 'message' | 'severity'> & {
  category?: string;
  timestamp: Date | string;
  message: string;
  severity: string;
};

// TODO(openapi): AlertRule has no canonical home yet — see Wave 4 backlog
interface AlertRule {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  severity: Alert['severity'];
  conditions: string;
  cooldown: number; // minutes
  lastTriggered?: Date;
}

// Mock data
const generateMockAlerts = (): Alert[] => [
  {
    id: 'alert-1',
    title: 'High CPU Usage Detected',
    message: 'Node node-alpha-1 CPU usage has exceeded 90% for the last 5 minutes',
    severity: 'critical',
    source: 'monitoring.cpu',
    timestamp: new Date(Date.now() - 2 * 60 * 1000),
    acknowledged: false,
    resolved: false,
    category: 'performance',
    affectedNodes: ['node-alpha-1'],
    metadata: { cpuUsage: 94.2, threshold: 90 }
  },
  {
    id: 'alert-2',
    title: 'Network Latency Spike',
    message: 'Inter-node communication latency increased to 450ms',
    severity: 'high',
    source: 'network.latency',
    timestamp: new Date(Date.now() - 15 * 60 * 1000),
    acknowledged: true,
    resolved: false,
    category: 'network',
    affectedNodes: ['node-beta-2', 'node-gamma-3'],
    metadata: { latency: 450, threshold: 200 }
  },
  {
    id: 'alert-3',
    title: 'Storage Space Warning',
    message: 'Available storage on cluster is below 20%',
    severity: 'medium',
    source: 'storage.capacity',
    timestamp: new Date(Date.now() - 45 * 60 * 1000),
    acknowledged: true,
    resolved: true,
    category: 'storage',
    metadata: { availableSpace: 18, threshold: 20 }
  },
  {
    id: 'alert-4',
    title: 'Consensus Leader Election',
    message: 'New leader elected after node failure',
    severity: 'info',
    source: 'consensus.election',
    timestamp: new Date(Date.now() - 60 * 60 * 1000),
    acknowledged: true,
    resolved: true,
    category: 'system',
    metadata: { newLeader: 'node-beta-2', previousLeader: 'node-alpha-1' }
  },
  {
    id: 'alert-5',
    title: 'Failed Authentication Attempts',
    message: 'Multiple failed login attempts detected from IP 192.168.1.100',
    severity: 'high',
    source: 'security.auth',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    acknowledged: false,
    resolved: false,
    category: 'security',
    metadata: { attemptCount: 15, sourceIP: '192.168.1.100' }
  }
];

const mockAlertRules: AlertRule[] = [
  {
    id: 'rule-1',
    name: 'High CPU Usage',
    description: 'Trigger when CPU usage exceeds 90% for 5 minutes',
    enabled: true,
    severity: 'critical',
    conditions: 'cpu_usage > 90 for 5m',
    cooldown: 10,
    lastTriggered: new Date(Date.now() - 2 * 60 * 1000)
  },
  {
    id: 'rule-2',
    name: 'Memory Pressure',
    description: 'Alert when memory usage is above 85%',
    enabled: true,
    severity: 'high',
    conditions: 'memory_usage > 85',
    cooldown: 5
  },
  {
    id: 'rule-3',
    name: 'Network Latency',
    description: 'Alert on high inter-node latency',
    enabled: true,
    severity: 'medium',
    conditions: 'network_latency > 200ms',
    cooldown: 15,
    lastTriggered: new Date(Date.now() - 15 * 60 * 1000)
  },
  {
    id: 'rule-4',
    name: 'Storage Capacity',
    description: 'Warn when storage space is low',
    enabled: true,
    severity: 'medium',
    conditions: 'storage_free < 20%',
    cooldown: 60
  },
  {
    id: 'rule-5',
    name: 'Node Offline',
    description: 'Alert when nodes become unreachable',
    enabled: false,
    severity: 'critical',
    conditions: 'node_status = offline for 30s',
    cooldown: 5
  }
];

export const AlertsPage: React.FC = () => {
  const [alerts] = useState<Alert[]>(generateMockAlerts());
  const [alertRules] = useState<AlertRule[]>(mockAlertRules);
  const [selectedTab, setSelectedTab] = useState<'active' | 'rules' | 'history'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showResolved, setShowResolved] = useState(false);

  const filteredAlerts = useMemo(() => {
    return alerts.filter(alert => {
      const matchesSearch = alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           alert.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           alert.source.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSeverity = severityFilter === 'all' || alert.severity === severityFilter;
      const matchesCategory = categoryFilter === 'all' || alert.category === categoryFilter;
      const matchesResolved = showResolved || !alert.resolved;

      return matchesSearch && matchesSeverity && matchesCategory && matchesResolved;
    });
  }, [alerts, searchTerm, severityFilter, categoryFilter, showResolved]);

  const activeAlerts = filteredAlerts.filter(alert => !alert.resolved);
  const criticalAlerts = activeAlerts.filter(alert => alert.severity === 'critical');
  const unacknowledgedAlerts = activeAlerts.filter(alert => !alert.acknowledged);

  const getSeverityIcon = (severity: Alert['severity']) => {
    switch (severity) {
      case 'critical': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'high': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'medium': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'low': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'info': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity: Alert['severity']): 'danger' | 'warning' | 'success' | 'default' => {
    switch (severity) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'warning';
      case 'low': return 'default';
      case 'info': return 'success';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category: Alert['category']) => {
    switch (category) {
      case 'system': return <Settings className="w-4 h-4" />;
      case 'security': return <Zap className="w-4 h-4" />;
      case 'performance': return <Activity className="w-4 h-4" />;
      case 'network': return <RefreshCw className="w-4 h-4" />;
      case 'storage': return <Download className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-console-text">Alert Management</h1>
          <p className="text-console-muted mt-1">Monitor and manage system alerts and notifications</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Create Rule
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-grid">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Active Alerts</p>
                <p className="text-2xl font-bold text-console-text">{activeAlerts.length}</p>
              </div>
              <Bell className="w-6 h-6 text-console-accent" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Critical</p>
                <p className="text-2xl font-bold text-red-500">{criticalAlerts.length}</p>
              </div>
              <XCircle className="w-6 h-6 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Unacknowledged</p>
                <p className="text-2xl font-bold text-orange-500">{unacknowledgedAlerts.length}</p>
              </div>
              <BellOff className="w-6 h-6 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Alert Rules</p>
                <p className="text-2xl font-bold text-console-text">{alertRules.filter(rule => rule.enabled).length}</p>
              </div>
              <Settings className="w-6 h-6 text-console-accent" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-console-border">
        <nav className="flex space-x-8">
          {[
            { id: 'active', label: 'Active Alerts', count: activeAlerts.length },
            { id: 'rules', label: 'Alert Rules', count: alertRules.length },
            { id: 'history', label: 'History', count: alerts.filter(a => a.resolved).length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === tab.id
                  ? 'border-console-accent text-console-accent'
                  : 'border-transparent text-console-muted hover:text-console-text hover:border-console-border'
              }`}
            >
              {tab.label}
              <span className="ml-2 bg-console-surface px-2 py-1 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      {selectedTab === 'active' && (
        <div className="flex items-center gap-4 p-4 bg-console-surface rounded-lg filter-bar">
          <div className="filter-input">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-console-muted" />
              <input
                type="text"
                placeholder="Search alerts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-console-bg border border-console-border rounded-lg text-console-text placeholder-console-muted focus:ring-2 focus:ring-console-accent focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="filter-select px-3 py-2 bg-console-bg border border-console-border rounded-lg text-console-text focus:ring-2 focus:ring-console-accent focus:border-transparent"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="info">Info</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="filter-select px-3 py-2 bg-console-bg border border-console-border rounded-lg text-console-text focus:ring-2 focus:ring-console-accent focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="system">System</option>
            <option value="security">Security</option>
            <option value="performance">Performance</option>
            <option value="network">Network</option>
            <option value="storage">Storage</option>
          </select>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowResolved(!showResolved)}
          >
            {showResolved ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showResolved ? 'Hide Resolved' : 'Show Resolved'}
          </Button>
        </div>
      )}

      {/* Content */}
      <div className="space-y-4">
        {selectedTab === 'active' && (
          <AnimatePresence>
            {filteredAlerts.map((alert, index) => (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`${alert.resolved ? 'opacity-60' : ''} ${
                  alert.severity === 'critical' ? 'border-red-500/50' : ''
                }`}>
                  <CardContent className="p-4">
                    <div className="alert-card flex flex-col sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getSeverityIcon(alert.severity)}
                          {getCategoryIcon(alert.category)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-console-text">{alert.title}</h3>
                            <Badge variant={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                            {alert.acknowledged && <Badge variant="success">Acknowledged</Badge>}
                            {alert.resolved && <Badge variant="default">Resolved</Badge>}
                          </div>
                          
                          <p className="text-console-muted mb-2">{alert.message}</p>
                          
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-console-muted">
                            <span>Source: {alert.source}</span>
                            <span>{formatRelativeTime(alert.timestamp)}</span>
                            {alert.affectedNodes && (
                              <span className="hidden sm:inline">Nodes: {alert.affectedNodes.join(', ')}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="alert-actions flex items-center gap-2 mt-3 sm:mt-0 sm:ml-4">
                        {!alert.acknowledged && (
                          <Button variant="secondary" size="sm">
                            <span className="hidden sm:inline">Acknowledge</span>
                            <span className="sm:hidden">Ack</span>
                          </Button>
                        )}
                        {!alert.resolved && (
                          <Button variant="primary" size="sm">
                            Resolve
                          </Button>
                        )}
                        <Button variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        )}

        {selectedTab === 'rules' && (
          <div className="space-y-4">
            {alertRules.map((rule, index) => (
              <motion.div
                key={rule.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-console-text">{rule.name}</h3>
                          <Badge variant={getSeverityColor(rule.severity)}>{rule.severity}</Badge>
                          <Badge variant={rule.enabled ? 'success' : 'default'}>
                            {rule.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                        
                        <p className="text-console-muted mb-2">{rule.description}</p>
                        
                        <div className="flex items-center gap-4 text-sm text-console-muted">
                          <span>Conditions: <code className="bg-console-bg px-1 rounded">{rule.conditions}</code></span>
                          <span>Cooldown: {rule.cooldown}m</span>
                          {rule.lastTriggered && (
                            <span>Last triggered: {formatRelativeTime(rule.lastTriggered)}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button variant="secondary" size="sm">
                          Edit
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {selectedTab === 'history' && (
          <div className="space-y-4">
            {alerts.filter(alert => alert.resolved).map((alert, index) => (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="opacity-60">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center gap-2">
                        {getSeverityIcon(alert.severity)}
                        {getCategoryIcon(alert.category)}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-console-text">{alert.title}</h3>
                          <Badge variant={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                          <Badge variant="success">Resolved</Badge>
                        </div>
                        
                        <p className="text-console-muted mb-2">{alert.message}</p>
                        
                        <div className="flex items-center gap-4 text-sm text-console-muted">
                          <span>Source: {alert.source}</span>
                          <span>Resolved: {formatRelativeTime(alert.timestamp)}</span>
                          {alert.affectedNodes && (
                            <span>Nodes: {alert.affectedNodes.join(', ')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};